import { useEffect } from 'react';
import { useAppData } from '@/contexts/AppDataContext';
import { extractAppleAppId, resolveAppStoreUrl } from '@/lib/appStoreLinks';

export const AppMetaTags = () => {
  const { config } = useAppData();

  useEffect(() => {
    if (!config) return;

    const appleID = extractAppleAppId(config.appStore);
    const packageName = config.googlePlay?.packageName;
    const appName = config.name || 'App';
    const appStoreUrl = resolveAppStoreUrl(config.appStore);
    const playStoreUrl = config.googlePlay?.url || `https://play.google.com/store/apps/details?id=${packageName}`;
    const currentUrl = window.location.href;

    // Get existing meta tags from index.html
    const appleMeta = document.getElementById('apple-app-meta') as HTMLMetaElement;
    const googlePlayMeta = document.getElementById('google-play-meta') as HTMLMetaElement;
    const manifestLink = document.getElementById('app-manifest') as HTMLLinkElement;
    
    // App Links meta tags
    const applinksIosUrl = document.getElementById('applinks-ios-url') as HTMLMetaElement;
    const applinksIosId = document.getElementById('applinks-ios-id') as HTMLMetaElement;
    const applinksIosName = document.getElementById('applinks-ios-name') as HTMLMetaElement;
    const applinksAndroidUrl = document.getElementById('applinks-android-url') as HTMLMetaElement;
    const applinksAndroidPackage = document.getElementById('applinks-android-package') as HTMLMetaElement;
    const applinksAndroidName = document.getElementById('applinks-android-name') as HTMLMetaElement;
    const applinksWebUrl = document.getElementById('applinks-web-url') as HTMLMetaElement;

    // Only update if at least one app is configured
    if (appleID || packageName) {
      // 1. iOS Smart App Banner
      if (appleID && appleMeta) {
        appleMeta.content = `app-id=${appleID}`;
      }

      // 2. Android App Indexing
      if (packageName && googlePlayMeta) {
        googlePlayMeta.content = `app-id=${packageName}`;
      }

      // 3. App Links - iOS
      if (appleID) {
        if (applinksIosUrl) applinksIosUrl.content = currentUrl;
        if (applinksIosId) applinksIosId.content = appleID;
        if (applinksIosName) applinksIosName.content = appName;
      }

      // 4. App Links - Android
      if (packageName) {
        if (applinksAndroidUrl) applinksAndroidUrl.content = currentUrl;
        if (applinksAndroidPackage) applinksAndroidPackage.content = packageName;
        if (applinksAndroidName) applinksAndroidName.content = appName;
      }

      // 5. App Links - Web fallback
      if (applinksWebUrl) {
        applinksWebUrl.content = currentUrl;
      }

      // 6. Web App Manifest
      const manifestData = {
        name: appName,
        short_name: appName,
        description: `${appName} - Aplicativo`,
        start_url: '/',
        scope: '/',
        display: 'standalone',
        orientation: 'portrait-primary',
        theme_color: config.themeColors?.primary || '#000000',
        background_color: '#ffffff',
        prefer_related_applications: true,
        related_applications: [] as Array<{ platform: string; id?: string; url?: string }>,
        icons: config.icon?.light ? [
          {
            src: config.icon?.light,
            sizes: '512x512',
            type: 'image/png',
            purpose: 'any maskable'
          }
        ] : []
      };

      // Add Android app to manifest
      if (packageName) {
        manifestData.related_applications.push({
          platform: 'play',
          id: packageName,
          url: playStoreUrl
        });
      }

      // Add iOS app to manifest
      if (appleID) {
        manifestData.related_applications.push({
          platform: 'itunes',
          url: appStoreUrl
        });
      }

      // Update manifest link
      if (manifestLink) {
        manifestLink.href = `data:application/manifest+json,${encodeURIComponent(JSON.stringify(manifestData))}`;
      }
    } else {
      // Clear all tags if no apps configured
      if (appleMeta) appleMeta.content = '';
      if (googlePlayMeta) googlePlayMeta.content = '';
      if (manifestLink) manifestLink.href = '';
      if (applinksIosUrl) applinksIosUrl.content = '';
      if (applinksIosId) applinksIosId.content = '';
      if (applinksIosName) applinksIosName.content = '';
      if (applinksAndroidUrl) applinksAndroidUrl.content = '';
      if (applinksAndroidPackage) applinksAndroidPackage.content = '';
      if (applinksAndroidName) applinksAndroidName.content = '';
      if (applinksWebUrl) applinksWebUrl.content = '';
    }
  }, [config]);

  return null;
};

